/**
 * TaskAttachments — slideout section listing files attached to a task record
 * via the shared `useRecordAttachments`/`RecordAttachmentRow` infrastructure
 * (`@/lib/crm/record/attachments`).
 *
 * The entity type is always `ahoy_task`, which resolves file associations
 * through `ahoy_task_to_file` via the live CRM schema
 * (`useFileAssociation`, `@/lib/crm/hooks/useFileAssociation`).
 *
 * Read flow:
 *   - Reads outbound file associations off the record's CrmObject and
 *     renders each as a `RecordAttachmentRow` (hydrated via
 *     `useCrmObjectSnapshots`, batched store read).
 *
 * Write flow:
 *   - "Add" uploads a file then attaches it to the record
 *     (`fileService.uploadFile` + `fileService.attachToRecord`), mirroring
 *     `files-section.tsx`. The row appears only after both resolve.
 *   - Per-row download triggers a detached anchor click against the file's
 *     download endpoint — no new-tab/popup navigation, no DOM insertion.
 *   - "Download all" fires one detached anchor click per attachment
 *     synchronously within the click gesture (no timers, no cap), so every
 *     download stays inside the browser's transient user activation — a
 *     staggered/delayed fan-out would push later clicks outside that window
 *     and the browser would block them. Per-file buttons remain the fallback.
 *   - Detach emits an `ahoy_association_removed` event on the record (never
 *     deletes the underlying file entity), mirroring
 *     `NoteLinkedRecordsSection`'s badge-remove flow.
 */

import { Download01, Plus } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { useFilePicker } from '@/lib/base/file-upload';
import {
  RecordAttachmentRow,
  useRecordAttachments,
} from '@/lib/crm/record/attachments';
// TEMP DEBUG (attach-debug) — remove once the field report is resolved.
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { CollapsibleSection } from '../sections/collapsible-section';
import { SectionActionButton } from '../sections/section-action-button';

interface TaskAttachmentsProps {
  object: CrmObject;
  orgId: number;
}

export function TaskAttachments({ object, orgId }: TaskAttachmentsProps) {
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission('ahoy_file', 'ahoy_object_created');
  const canRemove = hasPermission('ahoy_task', 'ahoy_association_removed');

  const {
    fileEntityIds,
    isUploading,
    isSubmitting,
    uploadError,
    detachError,
    canDownloadAll,
    handleFile,
    clearUploadError,
    handleRemove,
    handleDownload,
    handleDownloadAll,
  } = useRecordAttachments(object, orgId, 'ahoy_task');

  // TEMP DEBUG (attach-debug) — remove once the field report is resolved.
  const handlePickerCancel = useCallback(() => {
    logger.warn('[attach-debug] chooser cancelled');
  }, []);

  const { filePickerInput, openFilePicker } = useFilePicker({
    onFileSelected: handleFile,
    onCancel: handlePickerCancel,
  });

  const handleAddClick = useCallback(() => {
    // TEMP DEBUG (attach-debug) — remove once the field report is resolved.
    logger.warn('[attach-debug] Add clicked — opening persistent picker input');
    clearUploadError();
    openFilePicker();
  }, [clearUploadError, openFilePicker]);

  const headerActions = useMemo(() => {
    const addButton = canAdd && (
      <SectionActionButton
        label={isUploading ? 'Uploading…' : 'Add'}
        icon={Plus}
        onClick={handleAddClick}
        disabled={isUploading || isSubmitting}
      />
    );
    const downloadAllButton = fileEntityIds.length >= 1 && (
      <SectionActionButton
        label='Download all'
        icon={Download01}
        onClick={handleDownloadAll}
        disabled={!canDownloadAll}
      />
    );
    if (!addButton && !downloadAllButton) {
      return undefined;
    }
    return (
      <div className='flex items-center gap-1'>
        {addButton}
        {downloadAllButton}
      </div>
    );
  }, [
    canAdd,
    isUploading,
    isSubmitting,
    handleAddClick,
    fileEntityIds.length,
    handleDownloadAll,
    canDownloadAll,
  ]);

  if (fileEntityIds.length === 0 && !canAdd) {
    return null;
  }

  return (
    <CollapsibleSection title='Attachments' actions={headerActions}>
      {filePickerInput}
      {uploadError && (
        <p className='text-xs md:text-sm text-[var(--color-text-error-primary)]'>
          {uploadError}
        </p>
      )}
      {detachError && (
        <p className='text-xs md:text-sm text-[var(--color-text-error-primary)]'>
          {detachError}
        </p>
      )}
      {fileEntityIds.length === 0 ? (
        <p className='text-xs md:text-sm text-fg-secondary'>
          No attachments yet
        </p>
      ) : (
        <div className='flex flex-col gap-2'>
          {fileEntityIds.map(id => (
            <RecordAttachmentRow
              key={id}
              entityId={id}
              orgId={orgId}
              onDownload={handleDownload}
              onRemove={
                canRemove && !isSubmitting && !isUploading
                  ? handleRemove
                  : undefined
              }
            />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
